import { animate, state, style, transition, trigger, } from '@angular/animations';
import { ChangeDetectionStrategy, Component, inject, type OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { Agent, AgentService, CompanyService, JsonbType, NotificationService } from '@xilo-mono/form-contracts';
import { type CoverageAmount, type DesiredAutoCoverageSchema, type PolicyCoverage, type RateQuote, RatesService, RatesStore } from '@xilo-mono/shared/quotes';
import { environment } from '@xilo-mono/xilo-services';
import { BehaviorSubject, catchError, combineLatest, exhaustMap, filter, finalize, first, interval, map, type Observable, of, ReplaySubject, shareReplay, takeUntil } from 'rxjs';
import { ClientService } from '../../services/client.service';

@Component({
  selector: 'app-soft-rate',
  templateUrl: './soft-rate.component.html',
  styleUrls: ['./soft-rate.component.scss'],
  animations: [
    trigger('state', [
      state(
        'visible',
        style({
          opacity: '1',
          transform: 'translateY(3%)',
        })
      ),
      state(
        'hidden',
        style({
          opacity: '0',
          transform: 'translateY(-1%)',
        })
      ),
      transition('* => visible', [animate('600ms ease-out')]),
      transition('visible => hidden', [animate('600ms ease-out')]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col w-full h-full bg-neutral-50',
  },
})
export class SoftRateComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new ReplaySubject<boolean>(1);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly params = this.route.snapshot.queryParams;
  private readonly ratesService = inject(RatesService);
  private readonly clientService = inject(ClientService);
  private readonly notificationService = inject(NotificationService);
  private readonly agentService = inject(AgentService);
  private readonly ratesStore = inject(RatesStore);
  private readonly clientId$ = this.route.queryParamMap.pipe(
    map((params) => params.get('clientId') || params.get('userId'))
  );
  private readonly token$ = of(localStorage.getItem('token'));
  private readonly companyService = inject(CompanyService);
  private readonly submissionId:string = this.params?.submissionId
  private readonly formViewKey:string  = this.params?.formId

  protected readonly quotes$: Observable<RateQuote[]> = combineLatest([
    this.token$,
    this.clientId$,
    this.companyService.getCompany().pipe(map((company) => company.id)),
    of(this.submissionId),
    of(this.formViewKey),
  ]).pipe(
    filter(([token, clientId, companyId, submissionId, formViewKey]) => !!token && !!clientId && !!companyId && !!submissionId && !!formViewKey),
    exhaustMap(([token, clientId, companyId, submissionId, formViewKey]) => {
      if (!token || !clientId || !companyId || !submissionId || !formViewKey) {
        return of([] as RateQuote[]);
      }
      return this.ratesService.getRateJobs(token, clientId, companyId, submissionId, formViewKey).pipe( // After delay, make API call
        finalize(() => this.isLoading$.next(false)),
        map((rates) => {
          const quotes: RateQuote[] = [];
          for (const rate of rates) {
            if (rate.status === 'Done' && ('botResponse' in rate.data)) {
              quotes.push({
                id: ('id' in rate) ? rate.id as number : crypto.randomUUID(),
                quotePrice: rate?.data?.botResponse?.quotePrice,
                quoteNumber: rate?.data?.botResponse?.quoteNumber,
                quotePriceRaw: rate?.data?.botResponse?.quotePriceRaw,
                policyCoverages: getPolicyCoverages(rate?.data?.AutoPersPolicy?.DesiredPolicy),
                vehicleCoverages: this.getVehicleCoverages(rate?.data?.Vehicles),
                carrier: rate.carrier,
                term: rate?.data?.AutoPersPolicy?.RenewalTerm
              });
            }
          }
          if (!quotes.length) {
            // Return to thank you page if no successful jobs
            this.router.navigate(['/form/thank-you'], {
              queryParams: this.params,
            });
          }
          this.getClient(clientId);
          return quotes;
        }),
        catchError(() => {
          // Return to thank you page if failed
          this.router.navigate(['/form/thank-you'], {
            queryParams: this.params,
          });
          return of([] as RateQuote[]);
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected readonly tabPosition$ = new BehaviorSubject(2);
  protected readonly details$ = new BehaviorSubject(1);

  protected readonly progressbarValue$ = new BehaviorSubject(0);
  private curSec = 0;
  protected readonly isLoading$ = new BehaviorSubject<boolean>(true);
  protected readonly state$ = new BehaviorSubject<'visible' | 'hidden'>('visible');

  protected readonly icons = {
    arrow_down: faAngleDown,
    arrow_up: faAngleUp,
  };
  client:JsonbType;
  agent:Agent;

  ngOnInit() {
    this.startTimer(200);
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  setTab(tabIndex: number) {
    this.tabPosition$.next(tabIndex);
  }

  viewDetails(index: number) {
    if (this.details$.value === index) {
      this.details$.next(null);
      this.state$.next('hidden');
    } else {
      this.details$.next(index);
      this.state$.next('visible');
    }
  }

  startTimer(seconds: number) {
    const time = seconds * 10;
    const timer$ = interval(100);
    const sub = timer$.pipe(takeUntil(this.destroyed$)).subscribe((sec) => {
      this.progressbarValue$.next(0 + (sec * 100) / time);
      this.curSec = sec;
      if (this.curSec === time) {
        sub.unsubscribe();
      }
    });
  }

  selectQuote(quote: RateQuote) {
    this.ratesStore.selectQuote(quote);
    const clientName = this.client.companyName || `${this.client?.firstName} ${this.client?.lastName}`;
    const clientXiloUrl = `${environment.dashboardUrl}/agent/prospect/${this.client.id}`;
    const agentName = `${this.agent?.firstName} ${this.agent?.lastName}`;
    const subject = `${clientName} has requested to Move Forward`;
    const body = `${clientName} has requested to move forward with the ${quote.carrier.name} quote. `;
    this.notificationService
      .sendCourierEmail({
        email: this.agent.email,
        template: 'client_portal_events',
        data: {
          subject,
          body,
          clientName,
          agentName,
          clientXiloUrl,
          clientId: this.client?.id,
          clientEmail: this.client?.email || '',
          clientPhone: this.client?.phone || ''
        },
      })
      .pipe(first(), takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.router.navigate(['/form/thank-you'], {
            queryParams: this.params,
          });
        },
        error: (error: Error) => {
          console.log('### FATAL ERROR: ', error);
          this.router.navigate(['/form/thank-you'], {
            queryParams: this.params,
          });
        },
      });
  }

  getClient(id: string) {
    this.clientService.getByClientId(+id)
    .pipe(takeUntil(this.destroyed$))
    .subscribe((client) => {
        this.client = client;
        this.getAgent(this.client.clientAgentId)
    });
  }

  getAgent(id: number) {
    this.agentService.getAgentEmailById(id).pipe(
      takeUntil(this.destroyed$)
    ).subscribe(a => {
      this.agent = a;
    });
  }

  displayPremium(quote:RateQuote) {
    if (quote?.term === '6') return quote?.quotePrice / 6;
    else if (quote?.term === '12') return quote?.quotePrice / 12;
    // Default Term to 12 if none is found
    else return quote?.quotePrice / 12;
  }

  getVehicleCoverages(vehicles:JsonbType[]) {
    if (!vehicles[0]) return [];
    const vehicleCoverages = [];
    if (vehicles[0].CollDeductible) {
      vehicleCoverages.push({
        description: 'Collision (Deductible)',
        value: `$${vehicles[0].CollDeductible}`
      })
    }
    if (vehicles[0].CompDeductible) {
      vehicleCoverages.push({
        description: 'Comprehensive (Deductible)',
        value: `$${vehicles[0].CompDeductible}`
      })
    }
    return vehicleCoverages;
  }
}


const mockVehicleCoverages: PolicyCoverage[] = [
  {
    description: 'Comprehensive (Deductible)',
    amounts: [
      {
        amount: 500,
        description: 'deductible',
      },
    ],
  },
  {
    description: 'Collision (Deductible)',
    amounts: [
      {
        amount: 500,
        description: 'deductible',
      },
    ],
  },
  {
    description: 'Car Rental Reimbursement',
    amounts: [
      {
        amount: 40,
        description: 'per day',
      },
      {
        amount: 1200,
        description: 'max',
      },
    ],
  },
  {
    description: 'Roadside Assistance',
    amounts: [],
  },
];

export function coverageOptionsToNumbers(coverage: string): number[] {
  if (!coverage?.length) {
    return [];
  }
  const options = coverage.split('/');
  return options.map((opt) => {
    try {
      const n = parseInt(opt, 10);
      if (n < 1000) {
        return n * 1000;
      }
      return n;
    } catch (_) {
      return 0;
    }
  }).filter(n => n > 0);
}

function getCoverageAmounts(coverage: string): CoverageAmount[] {
  const nums = coverageOptionsToNumbers(coverage);
  return nums.map((n) => ({
    amount: n,
  }));
}

export function getPolicyCoverages(coverage: DesiredAutoCoverageSchema): PolicyCoverage[] {
  const policyCoverages: PolicyCoverage[] = [];
  if (!coverage) return policyCoverages
  if (coverage.BodilyInjury) {
    policyCoverages.push({
      description: 'Bodily Injury',
      amounts: getCoverageAmounts(coverage.BodilyInjury),
    });
  }

  if (coverage.PropertyDamage) {
    policyCoverages.push({
      description: 'Property Damage',
      amounts: getCoverageAmounts(coverage.PropertyDamage),
    });
  }

  if (coverage.UninsuredMotorist) {
    policyCoverages.push({
      description: 'Uninsured Motorist',
      amounts: getCoverageAmounts(coverage.UninsuredMotorist),
    });
  }

  if (coverage.MedicalPayments) {
    policyCoverages.push({
      description: 'Medical Payments',
      amounts: getCoverageAmounts(coverage.MedicalPayments),
    });
  }

  if (coverage.BodilyInjuryPropertyDamage) {
    policyCoverages.push({
      description: 'Bodily Injury & Property Damage',
      amounts: getCoverageAmounts(coverage.BodilyInjuryPropertyDamage),
    });
  }

  if (coverage.UnderInsuredMotorist) {
    policyCoverages.push({
      description: 'Under-insured Motorist',
      amounts: getCoverageAmounts(coverage.UnderInsuredMotorist),
    });
  }

  if (coverage.AccidentalDeath) {
    policyCoverages.push({
      description: 'Accidental Death',
      amounts: getCoverageAmounts(coverage.AccidentalDeath),
    });
  }

  if (coverage.FuneralExpense) {
    policyCoverages.push({
      description: 'Funeral Expense',
      amounts: getCoverageAmounts(coverage.FuneralExpense),
    });
  }

  if (coverage.WorkLoss) {
    policyCoverages.push({
      description: 'Work Loss',
      amounts: getCoverageAmounts(coverage.WorkLoss),
    });
  }

  return policyCoverages;
}